import { configure, makeAutoObservable } from 'mobx';
import { makePersistable, isHydrated } from 'mobx-persist-store';

import defaultSettings from '../../settings.json';
import {
  getAgentInfo,
  getOperatorGeoToken,
  getOperatorUserInfo,
  getUserAvatarUrl,
  getUserInfo,
} from '@/api/modules/user';
import {
  clearOperatorToken,
  clearToken,
  setTokenToLocalStorage,
} from '@/utils/token';
import { appStore, loginStore } from '..';

export interface UserInfo {
  uuid?: string;
  isLogin?: boolean;
  agent_id?: number;
  nickname?: string;
  nick_name?: string; // 代运营返回的
  username?: string;
  name?: string;
  avatar?: string;
  avatar_color?: string;
  job?: string;
  organization?: string;
  location?: string;
  email?: string;
  permissions: Record<string, string[]>;
  currentTeam?: any;
}
export interface GlobalState {
  settings?: typeof defaultSettings;
  userInfo?: UserInfo;
  userLoading?: boolean;
  agent_id?: number;
  is_operator?: boolean;
  operator_id?: number;
}

configure({
  enforceActions: 'never', // 默认是observed、只能在action函数中修改、设置never、可以在任何函数中进行修改
});

class UserStore {
  settings =
    JSON.parse(localStorage.getItem('LoginStore') || '{}').userInfo?.settings
      ?.themeColor ?? defaultSettings;
  userInfo: UserInfo = { permissions: {} };
  agent_id = Number(localStorage.getItem('agent_id'));
  is_operator = localStorage.getItem('is_operator') === 'true';
  operator_id = Number(localStorage.getItem('operator_id')) || undefined;
  userLoading = false;
  currentTeam?: any = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    makePersistable(this, {
      name: 'UserStore', // 保存的name，用于在storage中的名称标识，只要不和storage中其他名称重复就可以
      properties: [
        'userInfo',
        'settings',
        'currentTeam',
        'agent_id',
        'is_operator',
        'operator_id',
      ], // 要保存的字段，这些字段会被保存在name对应的storage中，注意：不写在这里面的字段将不会被保存，刷新页面也将丢失：get字段例外。get数据会在数据返回后再自动计算
      storage: window.localStorage, // 保存的位置：看自己的业务情况选择，可以是localStorage，sessionStorage
    });
  }

  // 注意这个字段的使用：在页面useEffect的时候，如果你没有添加依赖数组（初始化执行），那么你可能拿不到computed计算的数据（原因是这时候还没有persist结束）
  // 这个属性还是比较重要的，因为它可以在页面上做判断，当页面load完成，get数据计算完成之后，isHydrated会置为true
  get isHydrated() {
    return isHydrated(this);
  }

  get isManagedLogin() {
    return !!this.agent_id || this.is_operator;
  }

  reset() {
    this.settings = defaultSettings;
    this.userInfo = { permissions: {} };
    this.clearAgentId();
    this.setOperatorLogin(false);
    this.setOperatorId(undefined);
    this.currentTeam = null;
    this.userLoading = false;
    appStore.setAid(0);
    clearToken();
    clearOperatorToken();
    loginStore.reset();
  }

  setLoading(v) {
    this.userLoading = v;
  }

  async getUserInfo() {
    // const search: any = new URLSearchParams(location.search);
    // const token = search.get('token') ?? localStorage.getItem('Token');
    try {
      const isAgent = !!localStorage.getItem('agent_id');
      const isOperator =
        this.is_operator || localStorage.getItem('is_operator') === 'true';
      const useAgentInfo = isAgent && !isOperator;
      const res: any = isOperator
        ? null
        : useAgentInfo
        ? await getAgentInfo()
        : await getUserInfo();
      console.log('res=================>', res);

      if (res?.code === 0) {
        if (useAgentInfo) {
          this.userInfo = {
            ...res.data.item,
            avatar: res.data.item.avatar
              ? (await getUserAvatarUrl(res.data.item.avatar)).data?.url
              : null,
          };
        } else {
          this.userInfo = {
            ...res.data,
            avatar: res.data.avatar
              ? (await getUserAvatarUrl(res.data.avatar)).data?.url
              : null,
          };
        }

        // setTokenToLocalStorage(token);
        // if (search.get('token')) {
        //   location.replace('/matrix');
        // }
      } else {
        this.userInfo = res.data;
        location.replace('/login');
      }
      return res.data;
    } catch (error) {
      console.log('error=================>', error);
    }
  }

  setAgentId(value) {
    this.agent_id = value;
    localStorage.setItem('agent_id', value.toString());
  }

  clearAgentId() {
    this.agent_id = undefined;
    localStorage.removeItem('agent_id');
  }

  setOperatorLogin(value: boolean) {
    this.is_operator = value;
    if (value) {
      localStorage.setItem('is_operator', 'true');
    } else {
      localStorage.removeItem('is_operator');
    }
  }

  setOperatorId(value?: number) {
    this.operator_id = value;
    if (value) {
      localStorage.setItem('operator_id', String(value));
    } else {
      localStorage.removeItem('operator_id');
    }
  }

  async selectOperatorCustomer(customer: any) {
    const teamId = Number(customer?.team_id ?? customer?.id);
    if (!teamId) {
      throw new Error('客户 team_id 无效');
    }
    console.log('teamId=============<', teamId);

    const res: any = await getOperatorGeoToken(teamId);
    const tokenData = res?.data;
    const token =
      (typeof tokenData === 'string' ? tokenData : tokenData?.token) ||
      tokenData?.geo_token ||
      res?.token;

    if (!token) {
      throw new Error('获取客户 Token 失败');
    }

    setTokenToLocalStorage(token);
    this.currentTeam = {
      ...customer,
      id: teamId,
      team_id: teamId,
    };
    await appStore.getConfig();
    return token;
  }

  setUserInfo(value) {
    this.userInfo = value;
  }

  setCurrentTeam(value) {
    this.currentTeam = value;
  }

  updateSetting(v) {
    this.settings = {
      ...this.settings,
      ...v,
    };
  }
}

const userStore = new UserStore();

export default userStore;
